import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import DiverHeadLogo from "../icons/diver-head-logo.js";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useAuth } from "../contexts/AuthContext";
import { useUserClaims } from "../hooks/useClaims";
import ThemeToggler from "../theme/components/ThemeToggler.js";

// Define all menu items with their required claims
const navigationMenuItems = [
  { text: "Home", href: "/", requiredClaims: [] }, // Everyone can see
  { text: "All Dive Sites List", href: "/sites", requiredClaims: [] },
  { text: "Add a Dive Site", href: "/newsite", requiredClaims: [] },
  { text: "Your Dashboard", href: "/favoriteDivesites", requiredClaims: ["isTier2User","isTier3User", "isTier4User", "isTier5User", "isAdmin" ] },
  
  // Tier-specific items (add new features here as needed)
  //{ text: "Pro Pack", href: "/pro-pack", requiredClaims: ["isTier5User", "isAdmin"] },
  //{ text: "Dive Trails", href: "/trails", requiredClaims: ["isAdmin"] },

 
];

const settingsMenuItems = [
  { 
    text: "Admin Dashboard", 
    href: "/admin", 
    requiredClaims: ["isAdmin"],
    icon: <AdminPanelSettingsIcon fontSize="small" sx={{ mr: 1 }} />,
    style: { color: 'primary.main' }
  },
  { text: "Profile", href: "/profile", requiredClaims: [] },
  { text: "Membership", href: "/membership", requiredClaims: [] },
  { text: "Logout", href: "/logout", requiredClaims: [] },
];

const loggedOutPages = [
  { text: "Home", href: "/" },
  { text: "Login", href: "/login" },
];

// Helper function to check if user has required claims (OR logic - user needs ANY of the required claims)
const hasRequiredClaims = (userClaims, requiredClaims) => {
  if (requiredClaims.length === 0) return true; // No requirements = everyone can see
  return requiredClaims.some(claim => userClaims[claim]); // User has at least one of the required claims
};

function ResponsiveAppBar() {
  const { currentUser } = useAuth();
  const userClaims = useUserClaims(); // Use the custom hook
  const navigate = useNavigate();
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);

  // Filter menu items based on user claims (only when not loading)
  const visibleNavigationItems = userClaims.loading 
    ? [] 
    : navigationMenuItems.filter(item => hasRequiredClaims(userClaims, item.requiredClaims));
  
  const visibleSettingsItems = userClaims.loading 
    ? [] 
    : settingsMenuItems.filter(item => hasRequiredClaims(userClaims, item.requiredClaims));



  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = (to) => {
    navigate(to);
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = (to) => {
    navigate(to);
    setAnchorElUser(null);
  };

  const nonLoggedInNav = (
    <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" }, justifyContent: 'flex-end', alignItems: 'center' }}>
      {loggedOutPages.map((page) => (
        <Button
          key={page.text}
          onClick={() => handleCloseNavMenu(page.href)}
          color="primary"
          variant="contained"
          sx={{ my: 2, display: "block", mr: 2 }}
        >
          {page.text}
        </Button>
      ))}
      <Tooltip title="Go to Profile / Login">
        <IconButton onClick={() => navigate('/login')} sx={{ p: 0 }}>
          <Avatar />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const loggedInNav = (
    <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
      {visibleNavigationItems.map((item) => (
        <Button
          key={item.text}
          onClick={() => handleCloseNavMenu(item.href)}
          color="primary"
          variant="contained"
          sx={{ my: 2, display: "block", mr: 2 }}
        >
          {item.text}
        </Button>
      ))}
    </Box>
  );

  const loggedInMobileNav = (
    <Menu
      id="menu-appbar"
      anchorEl={anchorElNav}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      open={Boolean(anchorElNav)}
      onClose={() => setAnchorElNav(null)}
      sx={{ display: { xs: "block", md: "none" } }}
    >
      {visibleNavigationItems.map((item) => (
        <MenuItem key={item.text} onClick={() => handleCloseNavMenu(item.href)}>
          <Typography sx={{ textAlign: "center" }}>{item.text}</Typography>
        </MenuItem>
      ))}
    </Menu>
  );

  const loggedOutMobileNav = (
    <Menu
      id="menu-appbar"
      anchorEl={anchorElNav}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      open={Boolean(anchorElNav)}
      onClose={() => setAnchorElNav(null)}
      sx={{ display: { xs: "block", md: "none" } }}
    >
      {loggedOutPages.map((page) => (
        <MenuItem key={page.text} onClick={() => handleCloseNavMenu(page.href)}>
          <Typography sx={{ textAlign: "center" }}>{page.text}</Typography>
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <AppBar position="sticky" top={0} sx={{ zIndex: 1100 }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: "none", md: "flex" },
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".3rem",
              color: "inherit",
              textDecoration: "none",
            }}
          >
            <DiverHeadLogo
              style={{ width: "70px", height: "70px", marginRight: "8px" }}
              fill="#ffffff"
            />
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="small"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            {currentUser ? loggedInMobileNav : loggedOutMobileNav}
          </Box>

          {currentUser ? loggedInNav : nonLoggedInNav}
          {currentUser && (
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, ml: 2 }}>
                  {currentUser?.photoURL ? (
                    <Avatar alt="Profile Avatar" src={currentUser.photoURL} />
                  ) : currentUser?.displayName ? (
                    <Avatar alt={currentUser.displayName}>
                      {currentUser.displayName.charAt(0).toUpperCase()}
                    </Avatar>
                  ) : (
                    <Avatar />
                  )}
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={() => setAnchorElUser(null)}
              >
                {visibleSettingsItems.map((item) => (
                  <MenuItem 
                    key={item.text}
                    onClick={() => handleCloseUserMenu(item.href)}
                    sx={item.style || {}}
                  >
                    {item.icon && item.icon}
                    <Typography sx={{ textAlign: "center" }}>{item.text}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}
          <ThemeToggler sx={{ ml: 2, display: { xs: 'none', md: 'flex' } }} />
        </Toolbar>
      </Container>
      

    </AppBar>
  );
}

export default ResponsiveAppBar;